'use memo';

import React from 'react';
import { StakeholderType } from '@urbanx/agx-ui-components';
import { AgxSpacer, AgxTypography } from '@urbanx/agx.ui-components2';
import type { PreparedSignatory } from 'utils/vendorBuyerUtil';

interface PreviewSignatoriesProps {
  buyers?: PreparedSignatory[];
  buyerSolicitor?: PreparedSignatory;
  vendors?: PreparedSignatory[];
  vendorSolicitor?: PreparedSignatory;
  stakeholders?: StakeholderType[];
}

const PreviewSignatories: React.FC<PreviewSignatoriesProps> = ({
  buyers = [],
  buyerSolicitor,
  vendors = [],
  vendorSolicitor,
  stakeholders = [],
}) => {
  const renderSignatory = (
    signatory: PreparedSignatory,
    stakeholderName: string
  ) => {
    const signatoryName =
      signatory.name ?? `${signatory.firstName} ${signatory.lastName}`;
    return (
      <AgxSpacer
        direction="horizontal"
        key={`${signatory.id}-${stakeholderName}`}
        gap="spacing-2xl"
        contentJustification="space-between"
        padding={{ top: 'xs', bottom: 'xs' }}
        className="borderBottom signatory-content"
      >
        <AgxSpacer direction="vertical" gap="spacing-sm">
          <AgxTypography
            id={`signatory-name-${signatory.id}`}
            type="label"
            size="medium"
          >
            {signatoryName}
          </AgxTypography>
          <AgxTypography
            id={`signatory-email-${signatory.id}`}
            type="caption"
            size="medium"
            color="var(--text-tertiary)"
          >
            {signatory.email}
          </AgxTypography>
        </AgxSpacer>
        <AgxSpacer direction="vertical" className="fitContent">
          <AgxTypography
            id={`signatory-role-${signatory.id}`}
            type="caption"
            size="medium"
            color="var(--text-tertiary)"
          >
            {stakeholderName}
          </AgxTypography>
        </AgxSpacer>
      </AgxSpacer>
    );
  };

  const getVendorBuyerStakeholderName = (
    signatory: PreparedSignatory,
    stakeholder: StakeholderType
  ) => {
    return signatory.type === stakeholder
      ? `${stakeholder} ${(signatory.vendorBuyerIndex ?? 0) + 1}`
      : `${signatory.type} (${stakeholder} ${(signatory.vendorBuyerIndex ?? 0) + 1})`;
  };

  return (
    <AgxSpacer direction="vertical" fill>
      {stakeholders.map(stakeholder => {
        if (stakeholder === StakeholderType.Buyer) {
          return (
            buyers &&
            buyers.length > 0 &&
            buyers.map(signatory => {
              return renderSignatory(
                signatory,
                getVendorBuyerStakeholderName(signatory, stakeholder)
              );
            })
          );
        }
        if (stakeholder === StakeholderType.BuyerSolicitor) {
          return (
            buyerSolicitor &&
            renderSignatory(buyerSolicitor, "Buyer's Solicitor")
          );
        }
        if (stakeholder === StakeholderType.Vendor) {
          return (
            vendors &&
            vendors.length > 0 &&
            vendors.map(signatory => {
              return renderSignatory(
                signatory,
                getVendorBuyerStakeholderName(signatory, stakeholder)
              );
            })
          );
        }
        if (stakeholder === StakeholderType.VendorSolicitor) {
          return (
            vendorSolicitor &&
            renderSignatory(vendorSolicitor, "Vendor's Solicitor")
          );
        }
        return null;
      })}
    </AgxSpacer>
  );
};

export default PreviewSignatories;
